import { Injectable } from '@angular/core';

import { loadScript } from '@app/core/utilities/dom.utilities';
import { getCurrentStage } from '@app/core/utilities/url.utilities';
import { environment } from '@env/environment';

import { Customer } from '../models/customer.models';
import { GoogleDataLayer } from '../models/google-tag-manager.models';
import { User } from '../models/user.models';
import { IntercomService } from './intercom.service';
import { LocaleService } from '@app/core/services/locale.service';

@Injectable({
  providedIn: 'root',
})
export class GoogleTagManagerService {
  private gtmLoaded = false;

  constructor(
    private ic: IntercomService,
    private ls: LocaleService,
  ) {}

  init(user: User, customer?: Customer): void {
    this.addToDataLayer({ ...this.userToDataLayer(user), ...this.customerToDataLayer(customer) });
  }

  private userToDataLayer(user: User): GoogleDataLayer {
    if (!user) return {} as GoogleDataLayer;

    return {
      userID: user.userId,
      userEmail: user.email,
      userIsSysAdmin: user.sysAdmin,
      user_hash: user.intercomUserHash || null,
      language_override: this.ls.language(),
    } as GoogleDataLayer;
  }

  private customerToDataLayer(customer: Customer): GoogleDataLayer {
    if (!customer) return {} as GoogleDataLayer;
    return {
      companyName: customer.name,
      customerID: customer.customerId,
      mainCustomerId: customer.mainCustomerId,
      userRole: customer.access?.replace('-', '_'),
    } as GoogleDataLayer;
  }

  private initDataLayer(dataLayer: Partial<GoogleDataLayer>): void {
    window.dataLayerGTM = [{ 'gtm.start': new Date().getTime(), event: 'gtm.js' }];
    window.dataLayerGTM.push(dataLayer);
    loadScript(
      `https://www.googletagmanager.com/gtm.js?id=${environment.app.stages[getCurrentStage()].gtm}&l=dataLayerGTM`,
    ).then(() => {
      this.gtmLoaded = true;
      this.ic.update(dataLayer);
    });
  }

  private addToDataLayer(dataLayer: Partial<GoogleDataLayer>): void {
    if (!window.dataLayerGTM) this.initDataLayer(dataLayer);
    else {
      window.dataLayerGTM.push(dataLayer);

      if (this.gtmLoaded) this.ic.update(dataLayer);
    }
  }
}
